// Authenticated API service that handles JWT tokens and token refresh

class ApiService {
  constructor() {
    // Use relative URL for API calls (will be proxied by nginx)
    this.baseURL = '/api';
  }

  async getAuthHeaders() {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : ''
    };
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const headers = await this.getAuthHeaders();
    
    const config = {
      ...options,
      headers: {
        ...headers,
        ...options.headers
      },
      credentials: 'include' // For refresh token cookie
    };

    let response = await fetch(url, config);

    // If token expired, try to refresh
    if (response.status === 401) {
      const refreshed = await this.refreshToken();
      if (refreshed) {
        // Retry the request with new token
        const newHeaders = await this.getAuthHeaders();
        config.headers = {
          ...newHeaders,
          ...options.headers
        };
        response = await fetch(url, config);
      }
    }

    return response;
  }

  async refreshToken() {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('access_token', data.access_token);
        return true;
      } else {
        // Refresh failed, redirect to login
        localStorage.removeItem('access_token');
        window.location.href = '/login';
        return false;
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      localStorage.removeItem('access_token');
      window.location.href = '/login';
      return false;
    }
  }

  // Master AWBs
  async getMasterAWBs(projectId, filters = {}) {
    const params = new URLSearchParams({
      project_id: projectId,
      ...filters
    });
    
    const response = await this.makeRequest(`/master-awbs/?${params}`);
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to fetch Master AWBs: ${response.statusText}`);
  }

  async createMasterAWB(data) {
    const response = await this.makeRequest('/master-awbs/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
    
    if (response.ok) {
      return await response.json();
    }
    
    // Get detailed error message from response
    let errorMessage = `Failed to create Master AWB: ${response.statusText}`;
    try {
      const errorData = await response.json();
      if (errorData.detail) {
        errorMessage = `Failed to create Master AWB: ${errorData.detail}`;
      }
    } catch (e) {
      // If response is not JSON, use statusText
    }
    throw new Error(errorMessage);
  }

  async createMasterAWBFromCSV(csvData) {
    const response = await this.makeRequest('/create-master-awbs-from-csv/', {
      method: 'POST',
      body: JSON.stringify(csvData)
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to create Master AWB from CSV: ${response.statusText}`);
  }

  async deleteMasterAWB(masterAwbId) {
    const response = await this.makeRequest(`/master-awbs/${masterAwbId}`, {
      method: 'DELETE'
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to delete Master AWB: ${response.statusText}`);
  }

  // Shipments
  async getShipments(projectId, filters = {}) {
    const params = new URLSearchParams({
      project_id: projectId,
      ...filters
    });
    
    const response = await this.makeRequest(`/shipments/?${params}`);
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to fetch shipments: ${response.statusText}`);
  }

  async updateShipment(shipmentId, data) {
    const response = await this.makeRequest(`/shipments/${shipmentId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to update shipment: ${response.statusText}`);
  }

  async updateTracking(projectId) {
    const response = await this.makeRequest(`/update-tracking/?project_id=${projectId}`, {
      method: 'POST'
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to update tracking: ${response.statusText}`);
  }

  async updateDeliveryDates(projectId) {
    const response = await this.makeRequest(`/update-latest-delivery/?project_id=${projectId}`, {
      method: 'POST'
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to update delivery dates: ${response.statusText}`);
  }

  async updateAwbTracking(projectId) {
    const response = await this.makeRequest(`/update-awb-tracking/?project_id=${projectId}`, {
      method: 'POST'
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to update AWB tracking: ${response.statusText}`);
  }

  // Projects
  async getProjects() {
    const response = await this.makeRequest('/auth/projects');
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to fetch projects: ${response.statusText}`);
  }

  // Admin endpoints
  async createUser(userData) {
    const response = await this.makeRequest('/auth/admin/users', {
      method: 'POST',
      body: JSON.stringify(userData)
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to create user: ${response.statusText}`);
  }

  async getUsers() {
    const response = await this.makeRequest('/auth/admin/users');
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to fetch users: ${response.statusText}`);
  }

  async getAdminProjects() {
    const response = await this.makeRequest('/auth/admin/projects');
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to fetch admin projects: ${response.statusText}`);
  }

  async createProject(projectData) {
    const response = await this.makeRequest('/auth/admin/projects', {
      method: 'POST',
      body: JSON.stringify(projectData)
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to create project: ${response.statusText}`);
  }

  async updateProject(projectId, projectData) {
    const response = await this.makeRequest(`/auth/admin/projects/${projectId}`, {
      method: 'PUT',
      body: JSON.stringify(projectData)
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to update project: ${response.statusText}`);
  }

  async deleteProject(projectId) {
    const response = await this.makeRequest(`/auth/admin/projects/${projectId}`, {
      method: 'DELETE'
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to delete project: ${response.statusText}`);
  }

  async getAssignments() {
    const response = await this.makeRequest('/auth/admin/assignments');
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to fetch assignments: ${response.statusText}`);
  }

  async assignUserToProject(assignment) {
    const response = await this.makeRequest('/auth/admin/assign-user', {
      method: 'POST',
      body: JSON.stringify(assignment)
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to assign user to project: ${response.statusText}`);
  }

  async removeUserFromProject(assignment) {
    const response = await this.makeRequest('/auth/admin/assign-user', {
      method: 'DELETE',
      body: JSON.stringify(assignment)
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to remove user from project: ${response.statusText}`);
  }

  async deleteUser(userId) {
    const response = await this.makeRequest(`/auth/admin/users/${userId}`, {
      method: 'DELETE'
    });
    
    if (response.ok) {
      return await response.json();
    }
    throw new Error(`Failed to delete user: ${response.statusText}`);
  }
}

export const apiService = new ApiService();